import { useFormikContext } from 'formik';
import { useContext, useEffect, useRef } from 'react';

import { SlabContext } from '@core/context/SlabContext';
import type { ProductsByPlantFormikType, ProjectFormikType } from '@core/formik/project';
import type { DistanceDurationPair } from '@core/types/api/DistanceDurationPair/DistanceDurationPair';
import type Enums from '@core/types/api/Enums';
import type { Plant } from '@core/types/api/Plant/Plant';
import { hasValue } from '@core/utils/equality';
import { withFirstProductRowPending } from '@core/utils/locationRows';
import { isDefaultUUID } from '@core/utils/uuid';
import { isValidLatitudeString, isValidLongitudeString } from '@core/utils/yupHelpers';
import { usePlantDistances } from '@web/hooks/plantsDistance/usePlantDistances';

type NearestLocation = {
  id: string;
  name: string;
  distanceUnit: Enums.Unit;
  distance: string | null;
  driveTime: string | null;
};

const nearestPlant = (
  plants: Plant[],
  distanceByPlantId: Map<string, DistanceDurationPair | null>,
): NearestLocation | null => {
  const sortedDistances = plants
    .filter((p) => distanceByPlantId.has(p.id))
    .map((p) => {
      const result = distanceByPlantId.get(p.id);
      const unit = p.deliveryCosts.driveDistanceUnit;
      return {
        id: p.id,
        name: p.name,
        distanceUnit: unit,
        distance: result?.distance.toUnit(unit).toString() ?? null,
        driveTime: result?.duration.value.toISO() ?? null,
        comparisonDistance: result?.distance.meters || Infinity, // undefined, null and 0 means that plant is unreachable
        comparisonDuration: result?.duration.value.toMillis() || Infinity, // the same
      };
    })
    .sort((a, b) => {
      const distanceDifference = a.comparisonDistance - b.comparisonDistance;
      const durationDifference = a.comparisonDuration - b.comparisonDuration;

      if (distanceDifference !== 0) {
        return distanceDifference;
      }
      if (durationDifference !== 0) {
        return durationDifference;
      }
      return a.name.localeCompare(b.name);
    });

  return sortedDistances[0] ?? null;
};

export const usePlantAutoselect = (): void => {
  const formikBag = useFormikContext<ProjectFormikType>();
  const isNewProject = isDefaultUUID(formikBag.values.id);
  const { address } = formikBag.values;
  const {
    userInfo: { tenant },
  } = useContext(SlabContext);
  const { locationAutoSelect } = tenant;

  const hasValidCoords =
    isValidLatitudeString(address.latitude) && isValidLongitudeString(address.longitude);

  const { plants, distanceByPlantId } = usePlantDistances({
    address: hasValidCoords ? address : undefined,
  });

  const plantsWithCoordsCount = plants.filter((p) =>
    hasValue(p.address.toLatLngPairString()),
  ).length;

  // Pass the latest value to the effect without adding it to deps.
  const productsByPlantsRef = useRef(formikBag.values.productsByPlants);
  productsByPlantsRef.current = formikBag.values.productsByPlants;

  const firstPlant = formikBag.values.productsByPlants[0];

  // If there is no location, we just add one.
  // If the existing autoselected location hasn't been touched by a user, change it.
  // If a user manually changes the location, autoSelected becomes false.
  const canOverwriteLocation = !firstPlant?.plant?.id || (firstPlant?.autoSelected ?? false);

  const canAutoSelect = locationAutoSelect || plantsWithCoordsCount === 1;

  useEffect(() => {
    if (!canAutoSelect || !isNewProject || !canOverwriteLocation) {
      return;
    }

    const nearest = nearestPlant(plants, distanceByPlantId);
    if (!nearest || nearest.id === productsByPlantsRef.current[0]?.plant?.id) {
      return;
    }

    const newEntry: ProductsByPlantFormikType = {
      plant: {
        id: nearest.id,
        option: { value: nearest.id, label: nearest.name },
      },
      plantDistance: nearest.distance,
      plantDistanceUnit: nearest.distanceUnit,
      plantDriveTime: nearest.driveTime,
      products: productsByPlantsRef.current[0]?.products ?? [],
      autoSelected: true,
    };

    const rest = productsByPlantsRef.current.slice(1);
    formikBag.setFieldValue('productsByPlants', [newEntry, ...rest]);
    formikBag.setStatus(
      withFirstProductRowPending(formikBag.status, 'productsByPlants.0.products', nearest.id),
    );
  }, [distanceByPlantId, canAutoSelect, isNewProject, canOverwriteLocation]);
};
